/* components/EastSlider.ts
Uses https://swiperjs.com/demos
Settings are supported, use the inline navigation, pagination and scrollbar for best results
CSS -> for each slider, recommend adding css for class 'swiper-slide' of:
    (100% / number of slides shown) - (((number of slides - 1) * gap) / number of slides)
    eg, 4 slides and 20px gap: width: calc(25% - 15px)
    eg, 6 slides and 15px gap: width: calc((100% / 6) - ((5 * 15px) / 6))
Don't have to add the 'swiper-slide' class to the slides but would be beneficial
Use within liquid as <east-slider>

Example
<east-slider
    navigation="true",
    pagination="true"
    settings='{"slidesPerView": 3, "loop": true, "pagination":{"type":"bullets"}}'
>
    <div class="swiper-slide">
        Slide Content Here
    </div>
    <div class="swiper-slide">
        Slide Content Here
    </div>...
</east-slider>
*/

import { Component, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
// eslint-disable-next-line max-len
import Swiper, { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, Controller, SwiperOptions, Mousewheel } from 'swiper';
import 'swiper/css/bundle';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';

@Component
export class EastSlider extends ThemeComponent {
    @Prop({ default: false }) navigation!: boolean;
    @Prop({ default: '' }) svg!: string;
    @Prop({ default: false }) pagination!: boolean;
    @Prop({ default: false }) clickablePagination!: boolean;
    @Prop({ default: false }) scrollbar!: boolean;
    @Prop({ default: null }) breakpoint!: number;
    @Prop({ default: null }) thumbnails!: string;
    @Prop({ default: false }) isThumbnailSlider!: boolean;
    @Prop({ default: false }) listenToVariantChange!: boolean;
    @Prop({ default: false }) ignoreOneSlideInViewClass!: boolean;
    @Prop({ default: -1 }) optionIndex!: number;
    @Prop() settings!: SwiperOptions;

    private _onSwiperInit = new SimpleEvent<Swiper>();
    get onSwiperInit() {
        return this._onSwiperInit.asEvent();
    }

    private _prevEl: HTMLElement | null = null;
    private _nextEl: HTMLElement | null = null;


    swiper: Swiper | undefined = undefined;

    init() {



        // Set default nav icon
        this.setDefaultNavIcon();

        // Add required classes to make slider work
        this.addSliderClasses();

        // Insert styling wrapper div to DOM
        this.addSlides();

        // Add elements if props defined
        if (this.navigation || this.pagination || this.scrollbar) {
            this.checkPropsAddElements();
        }

        // Check responsive breakpoint
        if (this.breakpoint !== null) {

            // Slider should init below our breakpoint
            const mmBreakpoint = window.matchMedia(`(min-width: ${this.breakpoint + 1}px)`);

            // Check breakpoint, either init or destroy slider
            const breakpointChecker = () => {
                if (mmBreakpoint.matches) {
                    if (this.swiper !== undefined) {
                        this.swiper.destroy(true, true);
                    }
                    return;
                } else {
                    this.swiper = this.initialiseSwiper();
                }
            };

            // Init listener and initial check
            breakpointChecker();
            mmBreakpoint.addListener(breakpointChecker);
            return;
        }

        // Check if this slider is a nav for another
        if (this.thumbnails !== null) {
            const parentSliderElement = document.querySelector(`${this.thumbnails}`);
            if (parentSliderElement && parentSliderElement.tagName === 'EAST-SLIDER') {
                const parentSliderEastSliderComponent = parentSliderElement as EastSlider;

                // Check if parent slider has already initialised
                if (parentSliderElement.classList.contains('swiper-initialized')) {

                    // Update swiper settings object
                    this.settings.thumbs = { swiper: parentSliderEastSliderComponent.swiper };

                    // Initialise swiper
                    this.swiper = this.initialiseSwiper();
                } else {

                    // Listen for init
                    // eslint-disable-next-line max-len
                    this.$subscribe(parentSliderEastSliderComponent.onSwiperInit, (parentSliderSwiperComponent: Swiper) => {

                        // Update swiper settings object
                        this.settings.thumbs = { swiper: parentSliderSwiperComponent };

                        // Initialise swiper
                        this.swiper = this.initialiseSwiper();
                    });
                }
            }
        } else {
            // Initialise swiper
            this.swiper = this.initialiseSwiper();
        }

        // Listen for variant change?
        if (this.listenToVariantChange && this.optionIndex !== -1) {
            const productOptionPicker = document.getElementById('product-form-option-picker');
            if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
                // eslint-disable-next-line max-len
                this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant | null) => {
                    let imageId = null;
                    if (variant?.featured_image) {
                        imageId = variant?.featured_image.id;
                    }
                    this.handleOnVariantchange(variant, imageId);
                });
            }
        }

        this.updateArrowVisibility();
    }

    handleOnVariantchange(variant: Shopify.Liquid.Variant | null, imageId: number | null) {
        // Ensure swiper has initialized
        if (this.swiper === undefined) {
            return;
        }

        // Only continue if we have variant data
        if (variant === null) {
            return;
        }

        // Define variables
        let matchingSlideIndex = null;

        // Find slide by image ID
        if (imageId !== null) {
            const slides = this.swiper.el.querySelectorAll('.swiper-slide');
            let slideIndex = 0;
            for (const slide of slides) {
                const slideAttributeImageId = slide.getAttribute('data-image-id');
                if (slideAttributeImageId !== null && slideAttributeImageId !== '') {
                    const slideImageId = parseInt(slideAttributeImageId, 10);
                    if (slideImageId == imageId) {
                        matchingSlideIndex = slideIndex;
                    }
                }
                slideIndex++;
            }
        }

        // Find slide by image alt
        if (matchingSlideIndex === null) {

            // Get option
            if (this.optionIndex < 1 || this.optionIndex > 3) {
                throw new Error(`Invalid option index: ${this.optionIndex}`);
            }
            const variantOptionKey = `option${this.optionIndex}` as ShopifyProductOptionKey;
            const option = variant[variantOptionKey].toLowerCase();

            // Find slide index
            const sliderImages = this.swiper.el.querySelectorAll('.swiper-slide img');
            let slideIndex = 0;
            for (const image of sliderImages) {
                const thisImage = image as HTMLImageElement;
                const alt = thisImage.alt.toLowerCase();
                if (alt === option) {
                    matchingSlideIndex = slideIndex;
                }
                slideIndex++;
            }
        }

        // Go to slide
        if (matchingSlideIndex !== null) {
            this.swiper.slideTo(matchingSlideIndex, 300);
        }
    }

    setDefaultNavIcon() {
        if (!this.svg && this.navigation) {
            this.svg = `
                <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="28" 
                    height="52" 
                    viewBox="0 0 28 52">
                    <g><g>
                    <path 
                        fill="none" 
                        stroke="#000" 
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-miterlimit="20" 
                        stroke-width="3" 
                        d="M2.5 26.5l23 23m0-47l-23 24"/>
                    </g></g>
                </svg>
            `;
        }
    }

    addSliderClasses() {
        this.classList.add('swiper');
        if (this.navigation) {
            this.classList.add('swiper--has-navigation');
        }
        if (this.pagination) {
            this.classList.add('swiper--has-pagination');
        }
        if (this.settings.slidesPerView && this.settings.slidesPerView === 1 && !this.ignoreOneSlideInViewClass) {
            this.classList.add('swiper--one-slide-in-view');
        }
        const children = this.children;
        for (const child of children) {
            if (!child.classList.contains('swiper-slide')) {
                child.classList.add('swiper-slide');
            }
        }
    }

    addSlides() {
        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        for (const child of Array.from(this.children)) {
            wrapper.appendChild(child);
        }
        this.appendChild(wrapper);
    }

    checkPropsAddElements() {
        const props = document.createElement('div');
        props.className = 'swiper-props';
        // Add navigation
        if (this.navigation) {
            const prevButton = document.createElement('button');
            prevButton.setAttribute('role', 'button');
            prevButton.setAttribute('type', 'button');
            prevButton.setAttribute('aria-label', 'Previous slide');

            prevButton.classList.add('swiper-prev');
            prevButton.classList.add('swiper-navigation');
            prevButton.classList.add('swiper-prev--hidden');
            prevButton.innerHTML = this.svg;

            props.appendChild(prevButton);
            this._prevEl = prevButton;
        }

        // Add pagination and assign type
        if (this.pagination || this.settings.pagination) {
            const paginationElem = document.createElement('div');
            paginationElem.classList.add('swiper-pagination');
            props.appendChild(paginationElem);
            const pagination = (typeof this.settings.pagination == 'object')
                ? this.settings.pagination.type
                : 'bullets';
            this.settings.pagination = {
                el: '.swiper-pagination',
                type: pagination,
                clickable: this.clickablePagination
            };
        }


        if (this.navigation) {
            const nextButton = document.createElement('button');
            nextButton.setAttribute('role', 'button');
            nextButton.setAttribute('type', 'button');
            nextButton.setAttribute('aria-label', 'Next slide');

            nextButton.classList.add('swiper-next');
            nextButton.classList.add('swiper-navigation');
            nextButton.innerHTML = this.svg;

            props.appendChild(nextButton);
            this.settings.navigation = { nextEl: '.swiper-next', prevEl: '.swiper-prev' };
            this._nextEl = nextButton;
        }

        // Add scrollbar
        if (this.scrollbar || this.settings.scrollbar) {
            const scrollbarElem = document.createElement('div');
            scrollbarElem.classList.add('swiper-scrollbar');
            this.appendChild(scrollbarElem);
            this.settings.scrollbar = {
                el: '.swiper-scrollbar',
                draggable: true,
                hide: false
            };
        }

        this.appendChild(props);
    }

    initialiseSwiper() {
        Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs, Controller, Mousewheel]);
        this.swiper = new Swiper(this, this.settings);

        // Dispatch event
        this._onSwiperInit.dispatch(this.swiper);

        // Force allow clicks for thumbanil slides
        if (this.isThumbnailSlider) {
            this.swiper.on('touchEnd', (s1: Swiper, e: unknown) => {
                // Swiper's typings are incorrect - missing allowClick
                // allowClick is an unsupported attribute, used to fix the issue of
                // thumbnail slider clicks not always changing the active slide index
                // issue noted more here: https://github.com/nolimits4web/swiper/issues/1623
                const s = s1 as Swiper & { allowClick: boolean };
                s.allowClick = true;
            });
        }

        // Ensure correct src is used if pre-load component exists
        const images = this.querySelectorAll('img[data-preload]');
        for (const image of images) {
            if (image.getAttribute('data-preload') !== 'loaded') {
                const src = image.getAttribute('data-preload')
                    ? image.getAttribute('data-preload')
                    : image.getAttribute('src');
                if (src) {
                    image.setAttribute('src', src);
                    image.setAttribute('data-preload', 'loaded');
                }
            }
        }

        this.swiper.on('slideChange', () => {
            this.updateArrowVisibility();
        });

        // Return slider
        return this.swiper;
    }

    updateArrowVisibility() {
        const prevEl = this._prevEl || this.querySelector('.swiper-prev');
        const nextEl = this._nextEl || this.querySelector('.swiper-next');

        if (!prevEl || !nextEl) {return;}

        if (this.swiper) {
            prevEl.classList.toggle('swiper-prev--hidden', this.swiper.isBeginning);
            nextEl.classList.toggle('swiper-next--hidden', this.swiper.isEnd);
        }
    }
}
